import React, { useState, useMemo } from 'react';
import { compounds, categories } from '../data/compounds';
import CompoundCard from '../components/CompoundCard';
import './CompoundsIndex.css';

export default function CompoundsIndex() {
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('benefit');

  const filtered = useMemo(() => {
    const allCompounds = Object.values(compounds);
    return allCompounds
      .filter(c => {
        const q = search.toLowerCase();
        const matchSearch = !q || c.name.toLowerCase().includes(q) || c.fullName.toLowerCase().includes(q) || c.aliases?.some(a => a.toLowerCase().includes(q)) || c.summary.toLowerCase().includes(q);
        const matchCat = filterCat === 'all' || c.categoryId === filterCat;
        const matchStatus = filterStatus === 'all' || c.statusColor === filterStatus;
        return matchSearch && matchCat && matchStatus;
      })
      .sort((a, b) => {
        if (sortBy === 'benefit') return b.benefitScore - a.benefitScore;
        if (sortBy === 'harm') return a.harmScore - b.harmScore;
        if (sortBy === 'net') return (b.benefitScore - b.harmScore) - (a.benefitScore - a.harmScore);
        if (sortBy === 'name') return a.name.localeCompare(b.name);
        return 0;
      });
  }, [search, filterCat, filterStatus, sortBy]);

  const totalCount = Object.keys(compounds).length;

  return (
    <div className="ci-page">
      <div className="ci-header">
        <div className="ci-header-inner">
          <h1 className="ci-title">Research Compounds</h1>
          <p className="ci-subtitle">
            {totalCount} compounds across {categories.length} therapeutic categories.
            All compounds are currently unscheduled under federal DEA law.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="ci-filters-bar">
        <div className="ci-filters-inner">
          <div className="ci-search-wrap">
            <span className="ci-search-icon">🔍</span>
            <input
              className="ci-search"
              type="text"
              placeholder="Search compounds, aliases, or descriptions…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && <button className="ci-search-clear" onClick={() => setSearch('')}>✕</button>}
          </div>

          <select className="ci-select" value={filterCat} onChange={e => setFilterCat(e.target.value)}>
            <option value="all">All Categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
          </select>

          <select className="ci-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="green">Strongly Favored / Favored</option>
            <option value="orange">Exposure-Dependent</option>
            <option value="red">Caution</option>
          </select>

          <select className="ci-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="benefit">Sort: Benefit Score ↓</option>
            <option value="harm">Sort: Risk Score ↑</option>
            <option value="net">Sort: Net Value ↓</option>
            <option value="name">Sort: Name A–Z</option>
          </select>

          <div className="ci-result-count">
            {filtered.length} result{filtered.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="ci-content">
        <div className="ci-content-inner">
          {filtered.length === 0 ? (
            <div className="ci-empty">
              <div className="ci-empty-icon">🔬</div>
              <h3>No compounds match your filters</h3>
              <p>Try adjusting your search or filter criteria.</p>
              <button onClick={() => { setSearch(''); setFilterCat('all'); setFilterStatus('all'); }}>
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="ci-grid">
              {filtered.map(c => <CompoundCard key={c.id} compound={c} showCategory />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}